import { FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "../components/PasswordInput";
import { authApi, type SessionView, type UiTheme } from "../api/auth";
import { UI_THEMES } from "../lib/theme";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { AvatarUpload } from "../components/AvatarUpload";
import { FRONTEND_VERSION, buildsDiffer, formatVersion, useBackendVersion } from "../lib/appVersion";
import { formatDateTime } from "../lib/dates";

export default function ProfilePage() {
  const { state, refresh } = useAuth();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";

  if (state.status !== "authenticated") return null;
  const { user } = state;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 800 }}>
      <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-primary)" }}>
        {user.firstName} {user.lastName}
      </h1>

      {isWelcome && (
        <div
          style={{
            padding: 16,
            background: "var(--brand-primary-50)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            color: "var(--ink)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <strong>{t("profile.welcome.title", "Dobrodošli!")}</strong>
          <div style={{ marginTop: 4, color: "var(--ink-2)" }}>
            {t(
              "profile.welcome.body",
              "Dovršite podešavanje svog profila: otpremite fotografiju i proverite svoje podatke. Možete ih izmeniti u bilo kom trenutku.",
            )}
          </div>
        </div>
      )}

      <Card title={t("profile.photo.title", "Profilna fotografija")}>
        <AvatarUpload size={96} />
      </Card>

      <Card title={t("profile.account.title", "Nalog")}>
        <DetailRow label={t("profile.account.email", "Email")} value={user.email} />
        <DetailRow
          label={t("profile.account.roles", "Uloge")}
          value={user.roles.map((r) => r.name).join(", ") || "—"}
        />
      </Card>

      <ThemeCard />
      <PhoneCard onSuccess={refresh} />
      <ChangePasswordCard onSuccess={refresh} />
      <AboutCard />
      <SessionsCard />
      <span style={{ color: "var(--ink-4)", fontSize: 12 }}>{t("footer.copyright", "© {{year}} Bankom", { year: new Date().getFullYear() })}</span>
    </div>
  );
}

/** Serbian labels; rendered via t("profile.theme.<THEME>", fallback). */
const THEME_LABEL: Record<UiTheme, string> = { LIGHT: "Svetla", DARK: "Tamna", SYSTEM: "Kao sistem" };

function ThemeCard() {
  const { state, setTheme } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const current = state.status === "authenticated" ? state.user.theme : "SYSTEM";

  async function choose(theme: UiTheme) {
    setError(null);
    setSaving(true);
    try {
      await setTheme(theme);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title={t("profile.theme.title", "Izgled")}>
      <div className="chip-group" role="radiogroup" aria-label={t("profile.theme.title", "Izgled")}>
        {UI_THEMES.map((theme) => (
          <button
            key={theme}
            type="button"
            role="radio"
            aria-checked={current === theme}
            className={`chip${current === theme ? " active" : ""}`}
            disabled={saving}
            onClick={() => choose(theme)}
          >
            {t(`profile.theme.${theme}`, THEME_LABEL[theme])}
          </button>
        ))}
      </div>
      <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>
        {t("profile.theme.hint", "Važi samo za vaš nalog, na svakom uređaju na kom se prijavite. „Kao sistem“ prati podešavanje računara ili telefona.")}
      </span>
      {error && <Banner type="err">{error}</Banner>}
    </Card>
  );
}

function ChangePasswordCard({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) {
      setMsg({ type: "err", text: t("profile.password.mismatch", "Lozinke se ne podudaraju.") });
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setMsg({ type: "ok", text: t("profile.password.updated", "Lozinka je ažurirana. Ostale sesije su odjavljene.") });
      await onSuccess();
    } catch (err) {
      const apiErr = err as ApiError;
      setMsg({
        type: "err",
        text:
          apiErr.code === "CURRENT_PASSWORD_WRONG"
            ? t("profile.password.currentWrong", "Trenutna lozinka je netačna.")
            : apiErr.message || t("profile.password.error", "Promena lozinke nije uspela."),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card title={t("profile.password.title", "Promena lozinke")}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Input label={t("profile.password.current", "Trenutna lozinka")} type="password" value={current} onChange={setCurrent} required />
        <Input label={t("profile.password.new", "Nova lozinka")} type="password" value={next} onChange={setNext} required />
        <Input label={t("profile.password.confirm", "Potvrdi novu lozinku")} type="password" value={confirm} onChange={setConfirm} required />
        {msg && <Banner type={msg.type}>{msg.text}</Banner>}
        <button type="submit" disabled={submitting} style={primary}>
          {submitting ? t("common.saving", "Čuvanje…") : t("profile.password.submit", "Ažuriraj lozinku")}
        </button>
      </form>
    </Card>
  );
}

function SessionsCard() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SessionView[] | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      setSessions(await authApi.listSessions());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(id: string) {
    await authApi.revokeSession(id);
    await load();
  }

  return (
    <Card title={t("profile.sessions.title", "Aktivne sesije")}>
      {loading ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Učitavanje…")}</p>
      ) : !sessions || sessions.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("profile.sessions.empty", "Nema aktivnih sesija.")}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="list-card" style={table}>
            <thead>
              <tr>
                <th style={th}>{t("profile.sessions.device", "Uređaj")}</th>
                <th style={th}>{t("profile.sessions.ip", "IP")}</th>
                <th style={th}>{t("profile.sessions.issued", "Izdato")}</th>
                <th style={th}>{t("profile.sessions.lastUsed", "Poslednje korišćeno")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Akcije")}</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td style={td} data-label={t("profile.sessions.device", "Uređaj")}>{abbreviateUserAgent(s.userAgent, t("profile.sessions.unknownDevice", "Nepoznat uređaj"))}</td>
                  <td style={td} data-label={t("profile.sessions.ip", "IP")}>{s.ipAddress ?? "—"}</td>
                  <td style={td} data-label={t("profile.sessions.issued", "Izdato")}>{formatDate(s.issuedAt)}</td>
                  <td style={td} data-label={t("profile.sessions.lastUsed", "Poslednje korišćeno")}>{s.lastUsedAt ? formatDate(s.lastUsedAt) : "—"}</td>
                  <td className="card-actions" style={td}>
                    <button type="button" onClick={() => revoke(s.id)} style={dangerLink}>
                      {t("profile.sessions.revoke", "Opozovi")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function abbreviateUserAgent(ua: string | null, unknown: string) {
  if (!ua) return unknown;
  const m = ua.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/);
  const platform = ua.match(/\((Mac OS X|Windows|Linux|iPhone|Android)[^)]*\)/);
  return [m?.[0], platform?.[0]].filter(Boolean).join(" ") || ua.slice(0, 60);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("sr-Latn-RS");
}

function PhoneCard({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const { state } = useAuth();
  const { t } = useTranslation();
  const initial = state.status === "authenticated" ? state.user.phone ?? "" : "";
  const [phone, setPhone] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => setPhone(initial), [initial]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      await authApi.updateMe({ phone: phone.trim() || null });
      await onSuccess();
      setMsg({ type: "ok", text: t("profile.phone.saved", "Broj telefona je ažuriran.") });
    } catch (e) {
      const apiErr = e as ApiError;
      setMsg({ type: "err", text: apiErr?.message ?? String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("profile.phone.title", "Telefon")}>
      <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+381 11 123 4567"
          style={{ flex: 1, minWidth: 240, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14 }}
        />
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("common.saving", "Čuvanje…") : t("common.save", "Sačuvaj")}
        </button>
        {msg && (
          <span style={{ fontSize: 12, color: msg.type === "ok" ? "var(--ok)" : "var(--danger)" }}>
            {msg.text}
          </span>
        )}
      </form>
    </Card>
  );
}

/** Which build is in front of the user (2026-10-09): the bundle in the browser and the server, and whether they match. */
function AboutCard() {
  const { t } = useTranslation();
  const backend = useBackendVersion();
  const stale = buildsDiffer(FRONTEND_VERSION, backend);
  return (
    <Card title={t("profile.about.title", "O aplikaciji")}>
      <DetailRow label={t("profile.about.frontend", "Verzija u pregledaču")} value={formatVersion(FRONTEND_VERSION)} />
      <DetailRow label={t("profile.about.backend", "Verzija servera")} value={backend ? formatVersion(backend) : "…"} />
      <DetailRow label={t("profile.about.builtAt", "Izgrađeno")} value={backend?.buildTime ? formatDateTime(backend.buildTime) || backend.buildTime : "—"} />
      {stale && (
        <div className="muted" style={{ fontSize: 13 }} role="note">
          {t("profile.about.stale", "U pregledaču je starija verzija nego na serveru — osvežite stranicu (Ctrl+F5). Ako je aplikacija instalirana, zatvorite je i otvorite ponovo.")}
        </div>
      )}
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={card}>
      <h2 style={{ margin: 0, fontSize: 16, color: "var(--brand-primary)" }}>{title}</h2>
      {children}
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="form-grid-2" style={{ display: "grid", gridTemplateColumns: "140px 1fr", padding: "6px 0", fontSize: 14 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      <span style={{ color: "var(--ink)" }}>{value}</span>
    </div>
  );
}

function Input({
  label,
  type = "text",
  value,
  onChange,
  required,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      {type === "password" ? (
        <PasswordInput value={value} onChange={onChange} required={required} inputStyle={input} />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          style={input}
        />
      )}
    </label>
  );
}

function Banner({ type, children }: { type: "ok" | "err"; children: React.ReactNode }) {
  const bg = type === "ok" ? "rgba(31,138,86,.12)" : "rgba(182,53,58,.12)";
  const fg = type === "ok" ? "var(--ok)" : "var(--err)";
  return <div style={{ padding: 10, background: bg, color: fg, borderRadius: 6, fontSize: 13 }}>{children}</div>;
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  padding: 20,
  borderRadius: 10,
  boxShadow: "var(--shadow-1)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const input: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: 14,
};
const primary: React.CSSProperties = {
  padding: "8px 14px",
  background: "var(--brand-primary)",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  alignSelf: "flex-start",
};
const table: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: React.CSSProperties = { textAlign: "left", padding: "6px 8px", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 8px", borderBottom: "1px solid var(--surface-2)", color: "var(--ink-2)" };
const dangerLink: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--err)",
  fontSize: 13,
  cursor: "pointer",
  padding: 0,
};
